import * as Sentry from "@sentry/browser";
import type { Person } from "@/repositories/people/types";
import { initializeSentry } from "@/sentry.client.config";
import useBoundStore from "@/store/bound-store";
import usePersistedBoundStore from "@/store/persisted-bound-store";

const NODE_ENV: string = process.env.NODE_ENV || "development";

function setSentryUser(person: Partial<Person> | null): void {
  if (!person) {
    Sentry.setUser(null);
    Sentry.setTag("user.country", undefined);
    Sentry.setTag("user.profile_complete", undefined);
    Sentry.setTag("user.name", undefined);
    Sentry.setTag("user.email", undefined);
    Sentry.setTag("user.keycloak_id", undefined);
    return;
  }

  const fullName =
    person.firstName && person.lastName
      ? `${person.firstName} ${person.lastName}`
      : undefined;

  Sentry.setUser({
    id: person.id ? String(person.id) : undefined,
    email: person.mail || undefined,
    username: fullName,
    data: {
      keycloakId: person.keycloakId ?? undefined,
      identification: person.identification ?? undefined,
      country: person.country ?? undefined,
      gender: person.gender ?? undefined,
      completeProfile: person.completeProfile ?? undefined,
      hasSomeDisability: person.hasSomeDisability ?? undefined,
      studyArea: person.studyArea ?? undefined,
    },
  });

  // Tags — these show up in Discord alert notifications
  Sentry.setTag("user.country", person.country ?? undefined);
  Sentry.setTag("user.profile_complete", person.completeProfile != null ? String(person.completeProfile) : undefined);
  Sentry.setTag("user.name", fullName);
  Sentry.setTag("user.email", person.mail ?? undefined);
  Sentry.setTag("user.keycloak_id", person.keycloakId ?? undefined);
}

function initializeClientSentry(): void {
  if (NODE_ENV === "development") {
    return;
  }

  initializeSentry();

  const persistedState = usePersistedBoundStore.getState();
  const realm = persistedState.realm;

  if (realm) {
    Sentry.setTag("realm", realm);
  }

  const person = useBoundStore.getState().person;
  setSentryUser(person);

  // Subscribe to store changes so Sentry context updates after login
  let previousPerson: typeof person = person;
  useBoundStore.subscribe((state) => {
    const currentPerson = state.person;
    if (currentPerson !== previousPerson) {
      previousPerson = currentPerson;
      setSentryUser(currentPerson);
    }
  });

  let previousRealm: string | undefined = realm;
  usePersistedBoundStore.subscribe((state) => {
    const currentRealm = state.realm;
    if (currentRealm !== previousRealm) {
      previousRealm = currentRealm;
      if (currentRealm) {
        Sentry.setTag("realm", currentRealm);
      }
    }
  });
}

setTimeout(() => {
  initializeClientSentry();
}, 0);
